import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { LogIn, Loader2, AlertTriangle, Mail, CheckCircle2, KeyRound, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import useSEO from '../hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  useSEO({
    title: 'Accedi',
    description: 'Accedi alla piattaforma di emergenza LIFE LINKE. Gestisci le tue segnalazioni, chatta con gli operatori e invia check-in "sto bene".',
    noindex: false,
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Password recovery state
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [resetLink, setResetLink] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
  };

  const handleRecovery = async (e) => {
    e.preventDefault();
    if (!recoveryEmail) return;
    setRecoveryLoading(true);
    setResetLink('');
    try {
      const { data } = await axios.post(`${API}/auth/password-reset/request`, { email: recoveryEmail });
      if (data.link) {
        setResetLink(data.link);
        toast.success('Link di recupero generato!');
      } else {
        toast.info('Se l\'email è registrata, riceverai un link di recupero.');
      }
    } catch (e) {
      toast.error('Errore durante la richiesta');
    } finally {
      setRecoveryLoading(false);
    }
  };

  const LOGO = "https://customer-assets.emergentagent.com/job_help-beacon-9/artifacts/ok1e76vb_17%20apr%202026%2C%2009_06_17.png";

  return (
    <div className="min-h-screen bg-[#E8F4FD] pt-20 flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="border border-[#B8D4E8] bg-white p-8">
          <div className="flex items-center justify-center mb-8">
            <img src={LOGO} alt="LIFE LINKE" className="h-20 w-auto" />
          </div>

          {!showRecovery ? (
            <>
              <h1 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-[#0D1B2A] text-center mb-6">
                {t('accedi_account')}
              </h1>

              {error && (
                <div className="mb-4 p-3 border border-[#FF3B30] bg-[#FF3B30]/10 flex items-center gap-2" data-testid="login-error">
                  <AlertTriangle size={14} className="text-[#FF3B30]" />
                  <span className="text-xs text-[#FF3B30]">{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[#4A6B8A] mb-2">{t('email')}</label>
                  <input
                    type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                    className="w-full bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-3 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors text-sm"
                    data-testid="login-email-input"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[#4A6B8A] mb-2">{t('password')}</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required
                      className="w-full bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-3 pr-11 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors text-sm"
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      tabIndex={-1}
                      title={showPassword ? 'Nascondi password' : 'Mostra password'}
                      aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                      className="absolute inset-y-0 right-0 px-3 flex items-center text-[#4A6B8A] hover:text-[#FF3B30] transition-colors"
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <button type="submit" disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#FF3B30] text-white font-bold uppercase tracking-widest hover:bg-[#FF1A1A] transition-all disabled:opacity-50 text-sm"
                  data-testid="login-submit-btn">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />}
                  {t('login')}
                </button>
              </form>

              <div className="mt-4 text-center">
                <button
                  onClick={() => setShowRecovery(true)}
                  className="text-xs text-[#4A6B8A] hover:text-[#FF3B30] uppercase tracking-widest transition-colors"
                  data-testid="forgot-password-link"
                >
                  <KeyRound size={12} className="inline mr-1" />
                  Password dimenticata?
                </button>
              </div>

              <p className="mt-4 text-center text-xs text-[#6B8AB5]">
                {t('non_hai_account')}{' '}
                <Link to="/registrazione" className="text-[#FF3B30] hover:underline uppercase tracking-widest font-bold" data-testid="register-link">
                  {t('registrati')}
                </Link>
              </p>
            </>
          ) : (
            <>
              {/* Password Recovery Section */}
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-[#0D1B2A] text-center mb-2">
                <KeyRound size={18} className="inline mr-2 text-[#FF3B30]" />
                Recupera Password
              </h2>
              <p className="text-xs text-[#4A6B8A] text-center mb-6">
                Inserisci la tua email e riceverai un link per reimpostare la password
              </p>

              <form onSubmit={handleRecovery} className="space-y-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[#4A6B8A] mb-2">Email</label>
                  <input
                    type="email" value={recoveryEmail} onChange={(e) => setRecoveryEmail(e.target.value)} required
                    placeholder="La tua email registrata"
                    className="w-full bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-3 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors text-sm"
                    data-testid="recovery-email-input"
                  />
                </div>
                <button type="submit" disabled={recoveryLoading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#FF3B30] text-white font-bold uppercase tracking-widest hover:bg-[#FF1A1A] transition-all disabled:opacity-50 text-sm"
                  data-testid="send-reset-link-btn">
                  {recoveryLoading ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
                  Invia Link di Recupero
                </button>
              </form>

              {/* Reset Link Display */}
              {resetLink && (
                <div className="mt-4 p-4 border border-[#32D74B]/30 bg-[#32D74B]/5" data-testid="reset-link-box">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 size={14} className="text-[#32D74B]" />
                    <span className="text-xs text-[#32D74B] uppercase tracking-widest font-bold">Link generato</span>
                  </div>
                  <p className="text-[10px] text-[#4A6B8A] mb-2">Clicca il link qui sotto per reimpostare la password:</p>
                  <a
                    href={resetLink}
                    className="block text-xs text-[#0A84FF] hover:text-[#FF3B30] break-all underline font-mono"
                    data-testid="reset-link"
                  >
                    {resetLink}
                  </a>
                  <p className="text-[9px] text-[#B8D4E8] mt-2 uppercase tracking-widest">Il link scade tra 30 minuti</p>
                </div>
              )}

              <div className="mt-4 text-center">
                <button
                  onClick={() => { setShowRecovery(false); setResetLink(''); }}
                  className="text-xs text-[#4A6B8A] hover:text-[#FF3B30] uppercase tracking-widest transition-colors"
                  data-testid="back-to-login-btn"
                >
                  Torna al Login
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
